import type {
  LunaCardActions,
  LunaCardButton,
  LunaMessage,
  LunaMessageUpdate,
} from "./lunaSocket";

// Rules for a pending input's card in the Luna chat, kept free of React and the
// store so they can be tested on their own.

export const REASON_HEADING =
  "The agent reads your reason as its instruction for what to do instead.";
export const REASON_PLACEHOLDER = "What should the agent do instead?";
export const DEFAULT_REASON_MAX_LENGTH = 1000;

// Shown when action:answer never got a reply (channel error or timeout). The
// click may not have landed, so the card is re-enabled for another try.
export const UNREACHABLE_TEXT =
  "Your answer couldn't be sent. Check your connection and try again.";

// Applies a message:updated push to one session's messages. The push is
// authoritative and may arrive more than once, so content and actions are
// replaced outright. Returns null for a message that isn't loaded: the push
// can name one further back than the person has scrolled.
export const applyMessageUpdate = (
  messages: Record<string, LunaMessage> | undefined,
  update: LunaMessageUpdate,
): Record<string, LunaMessage> | null => {
  const existing = messages?.[update.id];
  if (!messages || !existing) return null;
  return {
    ...messages,
    [update.id]: {
      ...existing,
      content: update.content,
      actions: update.actions,
    },
  };
};

export const HISTORY_REFRESH_PAGE_SIZE = 100;
// A conversation longer than this many pages stops being refreshed past it,
// rather than looping on a has_more that never clears.
export const HISTORY_REFRESH_MAX_PAGES = 50;

interface HistoryPage<M> {
  messages: M[];
  is_thinking: boolean;
  has_more: boolean;
}

// Reads a conversation's history through to its newest message, handing each
// page to onPage as it arrives so a page is never held while pushes land.
// History comes back oldest-first with the offset counted from the start, so a
// refresh sized from what's loaded can stop short of a card that arrived by
// push. Offsets step by page size, not messages returned: the server can add
// entries to a page (the thread's opening chrome) that aren't rows. Returns the
// last page's thinking state and has_more.
export const readHistoryToEnd = async <M>(
  getPage: (limit: number, offset: number) => Promise<HistoryPage<M>>,
  onPage: (messages: M[]) => void,
  pageSize = HISTORY_REFRESH_PAGE_SIZE,
  maxPages = HISTORY_REFRESH_MAX_PAGES,
): Promise<Omit<HistoryPage<M>, "messages">> => {
  let last: HistoryPage<M> = { messages: [], is_thinking: false, has_more: false };
  for (let page = 0; page < maxPages; page++) {
    last = await getPage(pageSize, page * pageSize);
    onPage(last.messages);
    if (!last.has_more) break;
  }
  return { is_thinking: last.is_thinking, has_more: last.has_more };
};

// A card only moves forward: once retired (or shown as dashboard-only) it never
// goes back to live. A history read that started before the retiring push can
// still arrive after it, so a live copy of a settled card keeps the settled
// content and actions.
export const keepSettledCard = (
  existing: LunaMessage | undefined,
  incoming: LunaMessage,
): LunaMessage => {
  if (!existing?.actions || existing.actions.state === "live") return incoming;
  if (incoming.actions?.state !== "live") return incoming;
  return { ...incoming, content: existing.content, actions: existing.actions };
};

export const reasonMaxLength =(button: LunaCardButton): number =>
  button.reasonMaxLength ?? DEFAULT_REASON_MAX_LENGTH;

// The server trims the reason and refuses a blank one or one over the cap.
export const isReasonSendable = (reason: string, maxLength: number): boolean => {
  const trimmed = reason.trim();
  return trimmed.length > 0 && trimmed.length <= maxLength;
};

// Whether a card is waiting on this person. A dashboard card is information
// only and a retired card is done.
export const isCardLive = (actions: LunaCardActions): boolean =>
  actions.state === "live";

export interface RefusalHandling {
  // The card can't be answered here any more: leave its buttons disabled.
  finished: boolean;
  // The card may be stale: refetch the conversation.
  refetch: boolean;
  // The input is answered by typing a reply.
  focusComposer: boolean;
  // The text points at the dashboard: show the card's link with it.
  showLink: boolean;
}

const REFUSALS: Record<string, Partial<RefusalHandling>> = {
  not_found: { refetch: true },
  // Someone answered first. Refetch rather than wait for the message:updated
  // retiring the card: a closed chat gets no pushes.
  already_answered: { finished: true, refetch: true },
  reason_required: {},
  reason_too_long: {},
  choose_from_menu: {},
  not_a_menu_choice: { refetch: true },
  answer_by_reply: { focusComposer: true },
  not_allowed: { finished: true },
  no_longer_the_principal: { finished: true },
  needs_dashboard: { showLink: true },
  error: { refetch: true },
};

// What the card does after a refused click. An unknown code just shows its
// text and lets the person try again.
export const refusalHandling = (code: string): RefusalHandling => ({
  finished: false,
  refetch: false,
  focusComposer: false,
  showLink: false,
  ...REFUSALS[code],
});
